import * as React from 'react';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Box,
  Typography,
  ListItem,
  ListItemText,
  Chip,
  ListItemButton,
  IconButton,
  CircularProgress,
} from '@mui/material';

import { useFetchMeetings } from '../hooks/useFetchMeetings';
import { PaginationControls, StatusChip, DialogBox } from '../../../shared/components';
import { formatDate } from '../../../shared/utils/formatDate';
import AddIcon from '@mui/icons-material/Add';
import { CreateMeeting } from './Create/CreateMeeting';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { clientName } from '../../../theme/clientTheme';

const upcomingStatusColor = clientName === 'broadham-green' ? 'green' : 'amber';

interface MeetingsListProps {
  onMeetingClick: (id: string) => void;
}

const MeetingsList = React.forwardRef<{ refetch: () => void }, MeetingsListProps>(({ onMeetingClick }, ref) => {

  const navigate = useNavigate();
  const location = useLocation();

  const { isAllowedToAddMeeting } = useCapabilities();

  const [selectedIndex, setSelectedIndex] = React.useState<number | null | string>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filterType, setFilterType] = useState<'upcoming' | 'completed' | 'overdue'>('upcoming');

  const { meetingData, pagination, isLoading, isError, error, refetch } = useFetchMeetings({ projectId: null, page, limit, filterType });

  React.useImperativeHandle(ref, () => ({
    refetch
  }));

  const handleListItemClick = (meeting_id: string) => {
    setSelectedIndex(meeting_id);
    onMeetingClick(meeting_id);
    navigate(`?tab=meetingDetails&meeting_id=${meeting_id}`);
  };

  const [createMeetingOpen, setCreateMeetingOpen] = React.useState(false);

  const handleCreateMeetingOpen = () => setCreateMeetingOpen(true);

  const changePage = (page: number) => {
    setPage(page);
  };

  const changeRowsPerPage = (limit: number) => {
    setLimit(limit);
  };

  const handleFilterTypeChange = (type: 'upcoming' | 'completed' | 'overdue') => {
    setFilterType(type);
    setPage(1);
  };

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const meetingIdFromUrl = params.get('meeting_id');
    if (meetingIdFromUrl) {
      setSelectedIndex(meetingIdFromUrl);
      onMeetingClick(meetingIdFromUrl);
    }
  }, [location.search, onMeetingClick]);


  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          {error?.message || "Error loading data."}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="body1"
        >
          Meetings
        </Typography>
        {isAllowedToAddMeeting && <IconButton
          size="small"
          color="primary"
          aria-label="Create Meeting"
          onClick={handleCreateMeetingOpen}
        >
          <AddIcon />
        </IconButton>}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label="Upcoming"
            clickable
            variant={filterType === 'upcoming' ? 'filled' : 'outlined'}
            onClick={() => handleFilterTypeChange('upcoming')}
            color='primary'
          />
          <Chip
            label="Completed"
            clickable
            variant={filterType === 'completed' ? 'filled' : 'outlined'}
            onClick={() => handleFilterTypeChange('completed')}
            color='info'
          />
          <Chip
            label="Overdue"
            clickable
            variant={filterType === 'overdue' ? 'filled' : 'outlined'}
            onClick={() => handleFilterTypeChange('overdue')}
            color='error'
          />
        </Box>
      </Box>

      <Box flex={1} pl={2} pr={1} display={'flex'} flexDirection={'column'} width={'100%'} overflow={'auto'} gap={2}>
        {
          meetingData.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', mt: 4 }}
            >
              No meetings found.
            </Typography>
          )
            : meetingData.map((meeting: { id: string; category: string; title: string; status: string; start_date: string; start_time: string; end_time: string; }) => (
              <ListItem
                key={meeting.id}
                sx={{
                  border: 1,
                  borderColor: selectedIndex === meeting.id ? 'primary.main' : 'divider',
                  borderRadius: '8px',
                  padding: 0,
                }}
              >
                <ListItemButton
                  selected={selectedIndex === meeting.id}
                  onClick={() => handleListItemClick(meeting.id)}
                >
                  <ListItemText
                    primary={
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Typography variant='subtitle2'>
                              {meeting.category}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {meeting.title}
                            </Typography>
                          </Box>
                          {meeting?.status === 'Completed' ? (
                            <StatusChip
                              size="small"
                              label={'Completed'}
                              color='blue'
                            />
                          ) : meeting?.status === 'Overdue' ? (
                            <StatusChip
                              label={'Overdue'}
                              size="small"
                              color='red'
                            />
                          ) : (
                            <StatusChip
                              label={meeting?.status}
                              size="small"
                              color={upcomingStatusColor}
                            />
                          )}
                        </Box>
                      </>
                    }
                    secondary={
                      <Typography variant="caption">
                        {formatDate(meeting.start_date)} - {meeting.start_time} to {meeting.end_time}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
      </Box>

      <Box flexShrink={0}>
        <PaginationControls
          total={pagination?.total}
          rowsPerPage={pagination?.limit}
          currentPage={pagination?.page}
          handlePageChange={changePage}
          handleRowsPerPage={changeRowsPerPage}
        />
      </Box>

      <DialogBox
        title={'Create Meeting'}
        subTitle={''}
        children={
          <CreateMeeting
            onClose={() => { setCreateMeetingOpen(false); }}
            refetch={refetch}
            mode='create'
          />
        }
        showModal={createMeetingOpen}
        onModalClose={() => setCreateMeetingOpen(false)}
        fullWidth
      />

    </Box>
  );
});

MeetingsList.displayName = 'MeetingsList';

export default MeetingsList;
